/* eslint-disable no-unused-vars */
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';
import { ForgeAnomaly } from '@/scripts/js/forgeModels';
import { createTextGl, zoomToFitBox2D } from '@/utils/forgeUtils';
const THREE = window.THREE;

/**
 * Handler for annotation tags
 */
export class AnomaliesHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process annotation
     */
    shouldProcess(type, displaySettings) {
        return displaySettings.showAnomalies && type === 'anomaly';
    }

    /**
     * Collect annotation data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected annotation data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, isGlobalSearchTabShown, displaySettings } = settings;
        const data = {};
        const _this = this;

        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();

        // Skip if not applicable
        const type = this.extension.currentTagType || 'anomaly';
        if (!this.shouldProcess(type, displaySettings)) return data;

        // Get annotations for the current level
        let anomalies = this.isViewer2d()
            ? store.state.forgeService.getAnomaliesInLevel(store.state.selectedLevel)
            : store.getters.GET_ALL_EXISTING_ANOMALIES_TAGS;

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];
            if (isolatedTags.size > 0) {
                anomalies = anomalies.filter(t => isolatedTags.has(t.id));
            }
        }

        // Update with editing tags if in 2D view
        if (this.isViewer2d()) {
            anomalies = store.state.forgeService.updateListWithEditingTag(anomalies);

            // Filter annotations before clustering
            anomalies = anomalies.filter(anomaly => {
                // Skip if annotation is for a different level
                if (anomaly.level2D && (!this.isViewer2d() || anomaly.level2D != store.state.selectedLevel.name)) return false;

                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredAnomalies.includes(anomaly)) return false;
                */

                return true;
            });

            const anomaliesByCondition = {};

            anomalies.forEach(anomaly => {
                let color = ForgeAnomaly.getConditionColor(anomaly.condition);
                if (!anomaliesByCondition[color]) {
                    anomaliesByCondition[color] = [];
                }
                anomaliesByCondition[color].push(anomaly);
            });

            for (const [condition, objects] of Object.entries(anomaliesByCondition)) {
                if (objects.length > 0) {
                    const { items, clusters } = this.getTagsClustersAndItems2D(objects, viewerData.position, 1, true, true);

                    // Process clusters
                    this.processAnomalyConditionClustersByColor(clusters, data, condition);

                    items.forEach(item => {
                        // Get point in viewer space
                        let point = this.getPointForAnomaly(item);
                        if (!point) return;

                        // Create point cloud data
                        const pcData = this.createAnomalyData(item, point);

                        // Add to category collection
                        data[condition] ? data[condition].push(pcData) : data[condition] = [pcData];

                        // Draw tag area
                        this.drawTagArea(item);
                    });
                }
            }
        } else {
            // Filter annotations for 3D view
            anomalies = anomalies.filter(anomaly => {
                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredAnomalies.includes(anomaly)) return false;
                */

                return true;
            });

            anomalies.forEach(anomaly => {
                // Get point in viewer space
                let point = this.getPointForAnomaly(anomaly);
                if (!point) return;

                const pcData = {
                    name: anomaly.name,
                    thumbnailURL: anomaly.thumbnailUrl,
                    object: anomaly,
                    point: point,
                    onClick: (event) => {
                        store.dispatch('FETCH_ANOMALY_TAG_BY_ID', anomaly.id).then(tag => {
                            store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                        });
                    }
                };

                const color = ForgeAnomaly.getConditionColor(anomaly.condition);
                // Add to collection
                data[color] ? data[color].push(pcData) : data[color] = [pcData];

                // Draw tag area
                this.drawTagArea(anomaly);
            });
        }

        return data;
    }

    /**
     * Process clusters for a specific anomaly condition by color
     * @param {Array} clusters - Anomaly condition clusters
     * @param {Object} data - Data collection object
     * @param {string} pinColor - Pin color representing the condition
     */
    processAnomalyConditionClustersByColor(clusters, data, pinColor) {
        // Get font color based on pin color
        const fontColor = this.getFontColor(pinColor);

        clusters.forEach(cluster => {
            // Calculate average position for cluster
            const avgPoint = this.calculateAveragePoint(cluster);
            // Calculate the center point of the cluster
            let points = cluster.map(o => store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(o.point));

            // Create cluster data with type-specific key
            const clusterKey = `${pinColor}-cluster`;
            const clusterData = {
                name: cluster ? cluster.length : cluster.length,
                point: avgPoint,
                pinColor: pinColor,
                points: points,
                // Create a custom function for cluster text creation
                clusterCreate: () => {
                    // Draw text for cluster count if needed
                    let size = 12 / (this.extension.viewer.worldToClient(new THREE.Vector2(1, 0)).x -
                        this.extension.viewer.worldToClient(new THREE.Vector2(0, 0)).x);
                    let textPt = new THREE.Vector3(avgPoint.x, avgPoint.y, avgPoint.z + .05);

                    const textMesh = this.createTextMesh(
                        cluster ? cluster.length.toString() : cluster.length.toString(),
                        size,
                        fontColor,
                        textPt
                    );
                    this.clusterTextMeshes.push(textMesh);
                },
                onClick: () => {
                    // Handle click on cluster (zoom to bounds)
                    this.onClusterClick(cluster);
                }
            };

            // Add to collection with type-specific key
            data[clusterKey] ? data[clusterKey].push(clusterData) : data[clusterKey] = [clusterData];
        });
    }



    /**
     * Get point in viewer space for anomaly
     * @param {Object} anomaly - Anomaly object
     * @returns {Object|null} Point in viewer space
     */
    getPointForAnomaly(anomaly) {
        let point = this.isViewer2d()
            ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(anomaly.point)
            : anomaly.getPointInView();

        // Skip if outside crop box in 3D view
        if (!this.isViewer2d() && !store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
            return null;
        }

        return point;
    }

    /**
     * Create anomaly data for point cloud
     * @param {Object} anomaly - Anomaly object
     * @param {Object} point - Point in viewer space
     * @returns {Object} Point cloud data
     */
    createAnomalyData(anomaly, point) {
        const _this = this;
        return {
            name: anomaly.name,
            thumbnailURL: anomaly.thumbnailUrl,
            object: anomaly,
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_ANOMALY_TAG_BY_ID', anomaly.id).then(tag => {
                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                });
            }
        };
    }

    /**
     * Get font color based on pin color
     * @param {string} pinColor - Pin color
     * @returns {number} Font color
     */
    getFontColor(pinColor) {
        // Use white font color for dark pin colors using luminance calculation
        const luminance = this.calculateLuminance(pinColor);
        return luminance < 0.5 ? 0xffffff : 0x000000;
    }

    /**
     * Calculate luminance for a color
     * @param {string} color - Color value
     * @returns {number} Luminance value
     */
    calculateLuminance(color) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    }

    /**
     * Calculate average point from an array of items
     * @param {Array} items - Array of items with point property
     * @returns {Object} Average point (THREE.Vector3)
     */
    calculateAveragePoint(items) {
        if (!items || items.length === 0) {
            return new THREE.Vector3(0, 0, 0);
        }

        const sum = new THREE.Vector3(0, 0, 0);
        let count = 0;

        items.forEach(item => {
            if (item.point) {
                let point = this.getPointForAnomaly(item);
                sum.add(new THREE.Vector3(point.x, point.y, point.z));
                count++;
            }
        });

        if (count === 0) {
            return new THREE.Vector3(0, 0, 0);
        }

        return new THREE.Vector3(
            sum.x / count,
            sum.y / count,
            sum.z / count
        );
    }

    /**
     * Create text mesh for clusters
     * @param {string} text - Text to display
     * @param {number} size - Text size
     * @param {number} color - Text color
     * @param {Object} position - Text position
     * @returns {Object} Text mesh
     */
    createTextMesh(text, size, color, position) {
        // Create text for the cluster count
        const textMesh = createTextGl(text, size, color, position, this.extension.viewer, this.extension.sceneName);
        return textMesh;
    }

    /**
     * Handle click on a cluster
     * @param {Object} cluster - Cluster object
     */
    onClusterClick(cluster) {
        if (!this.isViewer2d()) return;

        // Create a bounding box containing all items in the cluster
        const items = cluster;
        const points = items.map(item => item.point).filter(point => !!point).map(point => {
            return store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(point)
        });

        if (points.length > 0) {
            let boundingBox = new THREE.Box3().setFromPoints(points);
            zoomToFitBox2D(this.extension.viewer, boundingBox);
        }
    }
}

